/**
 * Deployment Token Type Definitions
 *
 * TypeScript types for deployment token entities and operations.
 * Mirrors backend models from tenant-api/src/models/index.ts
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Deployment token status
 */
export const DeploymentTokenStatus = {
  ACTIVE: 'active',
  REVOKED: 'revoked',
} as const;

export type DeploymentTokenStatus = typeof DeploymentTokenStatus[keyof typeof DeploymentTokenStatus];

// ============================================================================
// DEPLOYMENT TOKEN ENTITY
// ============================================================================

/**
 * Deployment Token - Token for CI/CD pipeline authentication
 */
export interface DeploymentToken {
  tenantId: string;
  tokenId: string;
  applicationId: string;
  tokenCode: string;
  tokenName: string;
  tokenHash: string;
  tokenPrefix: string;
  status: DeploymentTokenStatus;
  expiresAt?: string; // ISO 8601 string
  revokedAt?: string;
  revokedBy?: string;
  lastUsedAt?: string;
  usageCount: number;
  createdAt: string;
  createdBy: string;
}

/**
 * Sanitized deployment token (for API responses - no hash)
 */
export interface DeploymentTokenSanitized {
  tenantId: string;
  tokenId: string;
  applicationId: string;
  tokenCode: string;
  tokenName: string;
  tokenPrefix: string;
  status: DeploymentTokenStatus;
  expiresAt?: string;
  revokedAt?: string;
  revokedBy?: string;
  lastUsedAt?: string;
  usageCount: number;
  createdAt: string;
  createdBy: string;
}

/**
 * Token creation response (includes raw token - shown only once!)
 */
export interface DeploymentTokenCreateResponse {
  tokenId: string;
  tokenCode: string;
  tokenName: string;
  tokenPrefix: string;
  token: string; // Full raw token - only returned on creation
  expiresAt?: string; // ISO 8601 string
  createdAt: string;
}

// ============================================================================
// DTOs
// ============================================================================

/**
 * DTO for deployment token creation
 */
export interface DeploymentTokenCreateDTO {
  tokenCode: string; // Unique code within application (lowercase alphanumeric + hyphens)
  tokenName: string; // Display name
  expiresInDays?: number; // Days until expiration (1-365, default 30)
}

// ============================================================================
// QUERY TYPES
// ============================================================================

/**
 * Deployment token list query parameters
 */
export interface DeploymentTokenListQuery {
  status?: DeploymentTokenStatus;
  limit?: number;
}

/**
 * List response type
 */
export interface DeploymentTokenListResponse {
  tokens: DeploymentTokenSanitized[];
  count: number;
  lastEvaluatedKey?: string;
}
